<script>
  // Hover tooltip listing items grouped by their parent path, same look as the LevelChip popover.
  // sections: [{ title, groups: [{ name, items: [string] }] }]
  import { tick } from "svelte";

  export let sections = [];

  const COLUMN_WIDTH = 288;
  const GAP = 4;
  const MARGIN = 8;

  let open = false;
  let popover;
  let style = "";

  $: itemCount = sections.reduce(
    (sum, section) =>
      sum + section.groups.reduce((total, group) => total + group.items.length, 0),
    0,
  );
  // Long lists spread over more columns instead of scrolling.
  $: columns = itemCount > 30 ? 3 : itemCount > 12 ? 2 : 1;

  // Fixed position so no container clips it. Opens upwards when there is more room above.
  async function show(event) {
    if (!itemCount) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const width = Math.min(columns * COLUMN_WIDTH, window.innerWidth - 2 * MARGIN);
    const widthStyle = columns === 1 ? `max-width: ${width}px;` : `width: ${width}px;`;
    const left = Math.max(MARGIN, Math.min(rect.left, window.innerWidth - width - MARGIN));
    const below = window.innerHeight - rect.bottom - GAP - MARGIN;
    const above = rect.top - GAP - MARGIN;
    style = `left: ${left}px; ${widthStyle} top: ${rect.bottom + GAP}px; max-height: ${below}px;`;
    open = true;
    await tick();
    if (popover && popover.scrollHeight > below && above > below)
      style = `left: ${left}px; ${widthStyle} bottom: ${window.innerHeight - rect.top + GAP}px; max-height: ${above}px;`;
  }
</script>

<div
  class="grouped-list-trigger"
  role="note"
  on:mouseenter={show}
  on:mouseleave={() => (open = false)}
>
  <slot />
</div>

{#if open}
  <div class="grouped-list-popover" role="tooltip" bind:this={popover} {style}>
    {#each sections as section}
      {#if section.groups.length}
        <div class="grouped-list-section">
          {#if section.title}<div class="grouped-list-title">
              {section.title} ({section.groups.reduce((total, group) => total + group.items.length, 0)})
            </div>{/if}
          <div class="grouped-list-groups" style="column-count: {columns};">
            {#each section.groups as group}
              <div class="grouped-list-group">
                {#if group.name}<div class="grouped-list-group-name">{group.name}</div>{/if}
                <ul class:nested={group.name}>
                  {#each group.items as item}<li>{item}</li>{/each}
                </ul>
              </div>
            {/each}
          </div>
        </div>
      {/if}
    {/each}
  </div>
{/if}

<style>
  .grouped-list-trigger {
    display: inline-flex;
    max-width: 100%;
  }

  .grouped-list-popover {
    position: fixed;
    z-index: 1000;
    min-width: 10rem;
    overflow-y: auto;
    padding: 0.6rem 0.75rem;
    border-radius: 4px;
    background: var(--pax-black, #161616);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    color: var(--pax-white, #fff);
    font-size: 0.8125rem;
    line-height: 1.35;
    white-space: normal;
    pointer-events: none;
  }

  .grouped-list-section + .grouped-list-section {
    margin-top: 0.5rem;
  }

  .grouped-list-title {
    margin-bottom: 0.35rem;
    font-weight: 600;
  }

  .grouped-list-groups {
    column-gap: 1rem;
  }

  .grouped-list-group {
    break-inside: avoid;
    padding-bottom: 0.4rem;
  }

  .grouped-list-group-name {
    opacity: 0.7;
  }

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  li {
    display: block;
  }

  ul.nested {
    padding-left: 0.75rem;
  }
</style>
